import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Camera, Download, Plus, Play, Square, Trash2, Upload } from "lucide-react";
import Button from "../ui/Button";
import Tooltip from "../ui/Tooltip";
import BehaviorPicker from "./BehaviorPicker";

const TOOLBAR_BTN =
  "inline-flex shrink-0 items-center gap-1.5 px-3 py-1 text-xs border border-gray-700 rounded-md hover:border-gray-600 transition-colors duration-150 shadow-none w-max max-w-none";
/** Icon-only control: same border treatment as TOOLBAR_BTN */
const TOOLBAR_ICON_BTN =
  "inline-flex shrink-0 items-center justify-center !w-8 !max-w-8 !h-8 !p-0 text-xs border border-gray-700 rounded-md hover:border-gray-600 transition-colors duration-150 shadow-none";
/** Single type scale for the header strip (buttons already use text-xs + font-medium via Button) */
const HEADER_BAR_TEXT = "text-xs font-medium";
/** Themed clusters: execution vs. current behavior */
const HEADER_GROUP =
  "flex min-w-0 items-center gap-2 rounded-lg border border-gray-800/90 bg-gray-950/40 px-2 py-1";

const STATUS_CONFIG = {
  idle: {
    label: "Ready",
    dot: "bg-neutral-500",
    text: "text-neutral-300",
    ring: "border-neutral-600",
    bg: "bg-neutral-800/60",
    animate: false,
  },
  running: {
    label: "Running",
    dot: "bg-amber-400",
    text: "text-amber-200",
    ring: "border-amber-500/40",
    bg: "bg-amber-500/10",
    animate: true,
  },
  starting: {
    label: "Starting physics...",
    dot: "bg-sky-400",
    text: "text-sky-200",
    ring: "border-sky-500/40",
    bg: "bg-sky-500/10",
    animate: true,
  },
  success: {
    label: "Succeeded",
    dot: "bg-emerald-400",
    text: "text-emerald-200",
    ring: "border-emerald-500/40",
    bg: "bg-emerald-500/10",
    animate: false,
  },
  failure: {
    label: "Failed",
    dot: "bg-rose-400",
    text: "text-rose-200",
    ring: "border-rose-500/40",
    bg: "bg-rose-500/10",
    animate: false,
  },
};

function resolveStatusKey(isRunning, isStartingPhysics, lastEpisodeOutcome, lastTickMessage) {
  if (isStartingPhysics) return "starting";
  if (isRunning) return "running";
  if (lastEpisodeOutcome === "success") return "success";
  if (lastEpisodeOutcome === "failure") return "failure";
  if (typeof lastTickMessage === "string" && lastTickMessage.startsWith("failure"))
    return "failure";
  if (typeof lastTickMessage === "string" && lastTickMessage.startsWith("success"))
    return "success";
  return "idle";
}

function StatusPill({
  isRunning,
  isStartingPhysics,
  lastEpisodeOutcome,
  lastTickMessage,
  activeNodeLabel,
}) {
  const key = resolveStatusKey(isRunning, isStartingPhysics, lastEpisodeOutcome, lastTickMessage);
  const cfg = STATUS_CONFIG[key];
  const suffix = isRunning && !isStartingPhysics && activeNodeLabel ? ` · ${activeNodeLabel}` : "";
  const failureDetail =
    key === "failure" &&
    typeof lastTickMessage === "string" &&
    lastTickMessage.startsWith("failure:")
      ? lastTickMessage.slice("failure:".length).trim()
      : "";
  const titleText = failureDetail ? `${cfg.label}: ${failureDetail}` : cfg.label;
  return (
    <Tooltip content={titleText}>
      <div
        className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 ${HEADER_BAR_TEXT} select-none outline-none ${cfg.bg} ${cfg.ring} ${cfg.text}`}
      >
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.animate ? "animate-pulse" : ""}`}
          aria-hidden="true"
        />
        <span className="truncate max-w-[300px]">
          {cfg.label}
          {suffix}
          {failureDetail ? ` · ${failureDetail}` : ""}
        </span>
      </div>
    </Tooltip>
  );
}

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      onOutside(e);
    };
    const onKey = (e) => {
      if (e.key === "Escape") onOutside(e);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, onOutside]);
}

function EditableName({ name, onCommit, disabled, emptyLabel }) {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  useEffect(() => {
    setValue(name);
  }, [name]);

  const commit = () => {
    setEditing(false);
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(name);
      return;
    }
    if (trimmed !== name) onCommit(trimmed);
  };

  if (emptyLabel && !name) {
    return (
      <span className={`min-w-0 flex-1 truncate italic text-gray-400 ${HEADER_BAR_TEXT}`}>
        {emptyLabel}
      </span>
    );
  }

  if (editing) {
    return (
      <div className="min-w-0 flex-1">
        <input
          autoFocus={!isMobile}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setValue(name);
              setEditing(false);
            }
          }}
          className={`w-full min-w-[120px] max-w-full rounded-sm border border-gray-600 bg-gray-900 px-2 py-0.5 text-gray-100 focus:border-sky-500 focus:outline-none ${HEADER_BAR_TEXT}`}
        />
      </div>
    );
  }

  const renameButton = (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && setEditing(true)}
      className={`group flex min-w-0 flex-1 items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-left text-gray-100 ${HEADER_BAR_TEXT} ${
        disabled ? "cursor-default" : "hover:bg-gray-700/60"
      }`}
    >
      <span className="min-w-0 flex-1 truncate">{name}</span>
    </button>
  );

  if (disabled) return renameButton;

  return (
    <Tooltip content="Click to rename" fullWidth>
      {renameButton}
    </Tooltip>
  );
}

function MenuRow({ icon: Icon, label, onClick, disabled, danger, onAfter }) {
  return (
    <button
      type="button"
      onClick={() => {
        if (disabled) return;
        onClick?.();
        onAfter?.();
      }}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-sm transition-colors ${
        disabled
          ? "panel-text-helper cursor-not-allowed"
          : danger
            ? "text-white bg-[var(--panel-surface-hover)] hover:!bg-red-500"
            : "text-gray-200 hover:bg-gray-700/80"
      }`}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function OverflowMenu({
  onCaptureBaseline,
  onExport,
  onImport,
  onDelete,
  canExport,
  canCaptureBaseline,
  canDelete,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close);

  return (
    <div ref={ref} className="relative">
      <Tooltip content="More actions">
        <button
          type="button"
          aria-label="More actions"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={`h-8 w-8 shrink-0 flex items-center justify-center rounded-md bg-transparent border-0 text-gray-200 hover:bg-gray-700 transition-colors cursor-pointer outline-none focus:outline-none ${
            open ? "bg-gray-700" : ""
          }`}
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </Tooltip>
      {open ? (
        <div
          className="absolute right-0 top-full mt-1 w-52 z-[10050] rounded-md border border-gray-600 p-1 shadow-xl"
          style={{ backgroundColor: "rgba(28, 28, 30, 0.98)", backdropFilter: "blur(6px)" }}
        >
          <MenuRow
            icon={Camera}
            label="Capture baseline"
            onClick={onCaptureBaseline}
            onAfter={close}
            disabled={!canCaptureBaseline}
          />
          <div className="my-1 border-t border-gray-800" />
          <MenuRow
            icon={Download}
            label="Export JSON"
            onClick={onExport}
            onAfter={close}
            disabled={!canExport}
          />
          <MenuRow icon={Upload} label="Import JSON" onClick={onImport} onAfter={close} />
          <div className="my-1 border-t border-gray-800" />
          <MenuRow
            icon={Trash2}
            label="Delete behavior"
            onClick={onDelete}
            onAfter={close}
            disabled={!canDelete}
            danger
          />
        </div>
      ) : null}
    </div>
  );
}

export default function BehaviorHeaderBar({
  behaviorList,
  unavailableTemplateIds = new Set(),
  selectedBehaviorId,
  behaviorName,
  isTemplate,
  onSelectBehavior,
  onDuplicateBehavior,
  onRenameBehavior,
  onRenameCurrent,
  onDeleteBehavior,
  onNew,
  isRunning,
  isStartingPhysics,
  canRun,
  onRun,
  onStop,
  lastEpisodeOutcome,
  lastTickMessage,
  activeNodeLabel,
  onCaptureBaseline,
  onExport,
  onImport,
  onDelete,
  canExport,
  canCaptureBaseline,
  canDelete,
}) {
  return (
    <div
      className="relative z-40 flex shrink-0 flex-nowrap items-center gap-3 border-b border-gray-800 px-2 py-2 min-w-0"
      style={{ backgroundColor: "rgba(18, 18, 20, 0.88)", backdropFilter: "blur(6px)" }}
    >
      <div className={`${HEADER_GROUP} shrink-0 flex-nowrap`}>
        {isRunning ? (
          <Button
            variant="secondary"
            size="small"
            title="Stop run"
            onClick={onStop}
            className={`${TOOLBAR_BTN} border-rose-700/50 text-rose-100 hover:border-rose-600/70 hover:bg-rose-950/35`}
          >
            <Square className="w-3.5 h-3.5" fill="currentColor" />
            Stop
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="small"
            title={
              isStartingPhysics
                ? "Starting physics..."
                : canRun
                  ? "Run this behavior (starts physics if stopped)"
                  : "Select a behavior to run"
            }
            onClick={onRun}
            disabled={!canRun || isStartingPhysics}
            className={TOOLBAR_ICON_BTN}
          >
            <Play className="w-3.5 h-3.5" fill="currentColor" aria-hidden="true" />
          </Button>
        )}

        <StatusPill
          isRunning={isRunning}
          isStartingPhysics={isStartingPhysics}
          lastEpisodeOutcome={lastEpisodeOutcome}
          lastTickMessage={lastTickMessage}
          activeNodeLabel={activeNodeLabel}
        />
      </div>

      <div className={`${HEADER_GROUP} flex-1 flex-nowrap`}>
        <Button
          variant="secondary"
          size="small"
          title="Build a new behavior"
          onClick={onNew}
          className={TOOLBAR_BTN}
        >
          <Plus className="w-3.5 h-3.5" />
          Build New
        </Button>

        <BehaviorPicker
          behaviorList={behaviorList}
          unavailableTemplateIds={unavailableTemplateIds}
          selectedId={selectedBehaviorId}
          currentNameHint={behaviorName}
          onSelect={onSelectBehavior}
          onDuplicate={onDuplicateBehavior}
          onRename={onRenameBehavior}
          onDelete={onDeleteBehavior}
        />

        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-hidden">
          <EditableName
            name={behaviorName}
            onCommit={onRenameCurrent}
            disabled={!selectedBehaviorId || isTemplate}
            emptyLabel="No behavior selected"
          />
          {isTemplate ? (
            <span
              className={`shrink-0 uppercase tracking-wider text-gray-400 border border-gray-700 rounded-sm px-1 py-[1px] ${HEADER_BAR_TEXT}`}
            >
              template
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 flex-nowrap items-center gap-1">
        <OverflowMenu
          onCaptureBaseline={onCaptureBaseline}
          onExport={onExport}
          onImport={onImport}
          onDelete={onDelete}
          canExport={canExport}
          canCaptureBaseline={canCaptureBaseline}
          canDelete={canDelete}
        />
      </div>
    </div>
  );
}
